import { QUERY_KEYS } from "@/libraries/constant";
import { getIsoDateTime } from "@/libraries/date";
import { unix } from "@/libraries/dayjs-utc";
import { intToHumanFileSize } from "@/libraries/helper";
import { collectMessagesTags } from "@/libraries/message";
import { applyDataChanges } from "@/libraries/transform";
import {
	type GetDeleteMessageBodySchema,
	MEMBER_ALERT_SCOPE,
	MESSAGE_TAG,
	type MessageDocumentSchema,
	type PatchUpdateMessageBodySchema,
	type PostCreateMessageBodySchema,
	type PostReadMessagePayloadSchema,
	type SyncMessageParamsSchema,
} from "@/schemas";
import { messageService } from "@/services";
import { useSyncStore } from "@/stores/sync";
import type {
	GetMemberAlertsResponse,
	GetMessageDiscussionsResponse,
	GetMessageResponse,
	MemberProfileResponse,
	MessageDiscussion,
} from "@/types";
import type { InfiniteData, QueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import isNumber from "lodash/isNumber";
import omit from "lodash/omit";
import uniqBy from "lodash/uniqBy";
import { generateUUID } from "pubnub";
import {
	infiniteDataAdd,
	infiniteDataRemove,
	infiniteDataReplace,
} from "../utils/mutation";

const postCreateMessage = (queryClient: QueryClient) => {
	queryClient.setMutationDefaults([QUERY_KEYS.MUTATE_CREATE_MESSAGE], {
		mutationFn: async (payload: PostCreateMessageBodySchema) => {
			const canUpload =
				payload.tags &&
				[MESSAGE_TAG.MEDIA, MESSAGE_TAG.FILES].includes(payload.tags as any);
			if (canUpload && Array.isArray(payload.files) && payload.files.length) {
				const bodyPresigns = Array.from(payload.files).map((file) => ({
					file,
					file_name: file.name,
				}));
				const fileService = await import("@/services/file").then(
					(mod) => mod.fileService,
				);
				const resPresigns = await fileService.presigns(bodyPresigns);
				if (resPresigns.length) {
					const fileNameFromFieldKey = await import("@/libraries/helper").then(
						(mod) => mod.fileNameFromFieldKey,
					);
					payload.documents = resPresigns.map(
						(presign, pIdx): MessageDocumentSchema => ({
							file_id: presign.fields.key,
							filename: fileNameFromFieldKey(presign.fields.key),
							display_name: bodyPresigns[pIdx].file_name,
							tags: payload.tags ?? MESSAGE_TAG.MEDIA,
							ocr_flag: false,
						}),
					);
				}
			}

			if (payload.tags === MESSAGE_TAG.PUBLISH && payload.preview) {
				if (payload.preview.bulletin_type === "file" && payload.files?.length) {
					const fileService = await import("@/services/file").then(
						(mod) => mod.fileService,
					);
					const resPresign = await fileService.avatarPublicPresign({
						file: payload.files[0],
						file_name: payload.files[0].name,
					});
					if (resPresign) {
						payload.preview.link = `${resPresign.url}/${resPresign.fields.key}`;
					}
				}
			}

			if (
				payload.tags &&
				[MESSAGE_TAG.MEDIA, MESSAGE_TAG.FILES].includes(
					payload.tags.toLowerCase() as any,
				)
			) {
				payload.tags = undefined;
			}

			if (!payload.id) {
				payload.id = generateUUID();
			}

			if (!payload.text) {
				payload.text = "";
			}

			const bodyPayload = omit(payload, ["files", "message", "document_tags"]);
			return messageService.create(bodyPayload);
		},
		retry: 2,
		onMutate: async (newMessage) => {
			const currentUser = queryClient.getQueryData<MemberProfileResponse>([
				QUERY_KEYS.GET_MEMBER_PROFILE,
			]);
			if (!currentUser) return;
			const queryKeys: Record<"channel_name" | "tags", string | undefined>[] = [
				{
					channel_name: newMessage.channel_name,
					tags: undefined,
				},
			];

			await queryClient.cancelQueries({
				queryKey: [
					QUERY_KEYS.GET_MESSAGES,
					{ channel_name: newMessage.channel_name },
				],
			});
			const currentDt = dayjs().unix();
			const currentStrDt = getIsoDateTime();
			const optimizeMessage: MessageDiscussion = {
				id: newMessage.id ?? generateUUID(),
				channel_name: newMessage.channel_name,
				user: currentUser.user,
				tags: newMessage.tags || null,
				is_active_user: false,
				accept_at: null,
				decline_at: null,
				system_message: false,
				unread_thread_messages: 0,
				id_db: -1,
				created_at: currentDt,
				updated_at: currentDt,
				reads: [...new Set([currentUser.user.id])],
				text: newMessage.text || null,
				documents: [],
				document_count: 0,
				document_size: "0.0 Kb",
				mentions: [],
				replies: [],
				preview: null,
				sent: false,
				strike: false,
				is_deleted: false,
				delivered: false,
				reply_to: null,
				invitation_status: null,
				latest_reply_date: null,
				reply_count: 0,
				mention_name: [],
				parent: null,
				mentioned_team_name: null,
				_flag: "sent",
				_isMe: true,
			};
			if (newMessage.preview) {
				optimizeMessage.preview = {
					...newMessage.preview,
					id: -1,
					created_at: currentStrDt,
					updated_at: currentStrDt,
				};
			}
			if (newMessage.documents?.length) {
				optimizeMessage.documents = newMessage.documents.map((doc, docIdx) => ({
					...doc,
					file_id: newMessage.files?.[docIdx]
						? URL.createObjectURL(newMessage.files[docIdx])
						: null,
					id: -1,
					mime_type: newMessage.files?.[docIdx]?.type || null,
					extension:
						newMessage.files?.[docIdx]?.name?.split(".")?.pop() || null,
					ocr_value: null,
					created_at: currentStrDt,
					updated_at: currentStrDt,
				}));
				optimizeMessage.document_count = newMessage.documents.length;
				const fileSize =
					newMessage.files?.reduce(
						(acc: number, file: File) => acc + file.size,
						0,
					) ?? 0;
				optimizeMessage.document_size = intToHumanFileSize(fileSize);
			}

			const messages = [optimizeMessage];
			if (messages.length) {
				const tags = collectMessagesTags(messages);
				if (tags.length) {
					for (const msgTag of tags) {
						queryKeys.push({
							channel_name: newMessage.channel_name,
							tags: msgTag,
						});
					}
				}
			}
			if (!newMessage.messageable_id) {
				for (const queryKey of queryKeys) {
					queryClient.setQueryData<InfiniteData<GetMessageDiscussionsResponse>>(
						[QUERY_KEYS.GET_MESSAGES, queryKey],
						(oldData) => infiniteDataAdd(oldData, "messages", optimizeMessage),
					);
				}
			}
			if (newMessage.messageable_id) {
				queryClient.setQueryData<GetMessageResponse>(
					[QUERY_KEYS.GET_MESSAGE, +newMessage.messageable_id],
					(oldData) =>
						oldData
							? {
									...oldData,
									message: {
										...oldData.message,
										messages: [optimizeMessage, ...oldData.message.messages],
									},
								}
							: undefined,
				);
			}
			return { queryKeys, optimizeMessage };
		},
		onSuccess: (data, newMessage, ctx) => {
			if (data.message.hemi_responses?.length) {
				const currTime = dayjs
					.unix(data.message.updated_at)
					.subtract(2, "seconds")
					.unix();
				const questionMesaage = {
					...ctx.optimizeMessage,
					id_db: data.message.id_db - 1,
					created_at: currTime,
					updated_at: currTime,
					_flag: "sent" as const,
				};
				const newMessages = [questionMesaage, data.message];
				for (const newMsg of newMessages) {
					queryClient.setQueryData<InfiniteData<GetMessageDiscussionsResponse>>(
						[
							QUERY_KEYS.GET_MESSAGES,
							{ channel_name: newMessage.channel_name, tags: MESSAGE_TAG.AI },
						],
						(oldData) => infiniteDataAdd(oldData, "messages", newMsg),
					);
				}
				return;
			}
			if (!newMessage.messageable_id) {
				for (const queryKey of ctx.queryKeys) {
					queryClient.setQueryData<InfiniteData<GetMessageDiscussionsResponse>>(
						[QUERY_KEYS.GET_MESSAGES, queryKey],
						(oldData) =>
							infiniteDataReplace(
								oldData,
								"messages",
								"id",
								ctx.optimizeMessage.id,
								data.message,
							),
					);
				}
				const channelNames = newMessage.channel_name?.split(".");
				const caseId =
					channelNames?.length && channelNames[0].startsWith("case")
						? channelNames.at(-1)
						: undefined;
				if (caseId) {
					queryClient.invalidateQueries({
						queryKey: [QUERY_KEYS.GET_CASE, +caseId],
					});
				}
			}
			if (newMessage.messageable_id) {
				queryClient.setQueryData<GetMessageResponse>(
					[QUERY_KEYS.GET_MESSAGE, +newMessage.messageable_id],
					(oldData) =>
						oldData
							? {
									...oldData,
									message: {
										...oldData.message,
										messages: [
											{ ...ctx.optimizeMessage, ...data.message },
											...oldData.message.messages.filter(
												(m) => m.id_db !== ctx.optimizeMessage.id_db,
											),
										],
									},
								}
							: undefined,
				);
			}
		},
		onError: (_error, newMessage, ctx) => {
			if (!ctx?.optimizeMessage) return;
			if (!newMessage.messageable_id) {
				for (const queryKey of ctx.queryKeys) {
					queryClient.setQueryData<InfiniteData<GetMessageDiscussionsResponse>>(
						[QUERY_KEYS.GET_MESSAGES, queryKey],
						(oldData) => ({
							...oldData,
							pages:
								oldData?.pages.map((page) => {
									const uniqueMessages = page.messages?.length
										? uniqBy(page.messages, "id")
										: [];
									const messages = uniqueMessages.map((msg) =>
										msg.id === ctx.optimizeMessage.id
											? {
													...msg,
													_flag: "failed" as const,
													_isMe: true,
												}
											: msg,
									);
									return {
										...page,
										messages,
									};
								}) ?? [],
							pageParams: oldData?.pageParams ?? [1],
						}),
					);
				}
			}
			if (newMessage.messageable_id) {
				queryClient.setQueryData<GetMessageResponse>(
					[QUERY_KEYS.GET_MESSAGE, +newMessage.messageable_id],
					(oldData) =>
						oldData
							? {
									...oldData,
									message: {
										...oldData.message,
										messages: oldData.message.messages.filter(
											(m) => m.id !== ctx.optimizeMessage.id,
										),
									},
								}
							: undefined,
				);
			}
		},
	});
};

const patchUpdateMessage = (queryClient: QueryClient) => {
	queryClient.setMutationDefaults([QUERY_KEYS.MUTATE_UPDATE_MESSAGE], {
		mutationFn: (payload: PatchUpdateMessageBodySchema) =>
			messageService.update(payload),
		retry: 2,
		onMutate: async (newMessage) => {
			const channel_name = newMessage.channelName;
			if (!channel_name) return;
			await queryClient.cancelQueries({
				queryKey: [QUERY_KEYS.GET_MESSAGES, { channel_name }],
			});
			await queryClient.cancelQueries({
				queryKey: [QUERY_KEYS.GET_MESSAGE, newMessage.messageable_id],
			});
			const queryKeys: Record<"channel_name" | "tags", string | undefined>[] = [
				{
					channel_name,
					tags: undefined,
				},
			];

			const resMessages = queryClient.getQueryData<
				InfiniteData<GetMessageDiscussionsResponse>
			>([QUERY_KEYS.GET_MESSAGES, queryKeys[0]]);

			if (resMessages?.pages?.length) {
				const messages = resMessages.pages.flatMap((p) => p.messages);
				if (messages.length) {
					const message = messages.find((m) => m.id_db === newMessage.id);
					if (message) {
						const tags = collectMessagesTags([message]);
						if (tags.length) {
							for (const msgTag of tags) {
								queryKeys.push({
									channel_name,
									tags: msgTag,
								});
							}
						}
					}
				}
			}

			if (isNumber(newMessage.messageable_id)) {
				queryClient.setQueryData<GetMessageResponse>(
					[QUERY_KEYS.GET_MESSAGE, newMessage.messageable_id],
					(oldData) =>
						!oldData
							? oldData
							: {
									...oldData,
									message: {
										...oldData.message,
										messages: oldData.message.messages.map((message) =>
											message.id_db === newMessage.id
												? {
														...message,
														...("delivered" in newMessage
															? { delivered: newMessage.delivered }
															: {}),
														...("strike" in newMessage
															? { strike: newMessage.strike }
															: {}),
														...("sent" in newMessage
															? { sent: newMessage.sent }
															: {}),
													}
												: message,
										),
									},
								},
				);
			} else {
				for (const queryKey of queryKeys) {
					queryClient.setQueryData<InfiniteData<GetMessageDiscussionsResponse>>(
						[QUERY_KEYS.GET_MESSAGES, queryKey],
						(oldData) => ({
							...oldData,
							pages:
								oldData?.pages.map((page) => ({
									...page,
									messages:
										page?.messages?.map((msg) => {
											if (msg.id_db === newMessage.id) {
												return {
													...msg,
													...("delivered" in newMessage
														? { delivered: newMessage.delivered }
														: {}),
													...("strike" in newMessage
														? { strike: newMessage.strike }
														: {}),
													...("sent" in newMessage
														? { sent: newMessage.sent }
														: {}),
												};
											}
											return msg;
										}) ?? [],
								})) ?? [],
							pageParams: oldData?.pageParams ?? [1],
						}),
					);
				}
			}

			return { queryKeys };
		},
		onError: (_error, newMessage, ctx) => {
			if (!ctx) return;
			const channel_name = newMessage.channelName;
			if (!channel_name) return;
			if (isNumber(newMessage.messageable_id)) {
				return queryClient.setQueryData<GetMessageResponse>(
					[QUERY_KEYS.GET_MESSAGE, newMessage.messageable_id],
					(oldData) =>
						!oldData
							? oldData
							: {
									...oldData,
									message: {
										...oldData.message,
										messages: oldData.message.messages.map((message) =>
											message.id_db === newMessage.id
												? {
														...message,
														...("delivered" in newMessage
															? { delivered: !newMessage.delivered }
															: {}),
														...("strike" in newMessage
															? { strike: !newMessage.strike }
															: {}),
														...("sent" in newMessage
															? { sent: !newMessage.sent }
															: {}),
													}
												: message,
										),
									},
								},
				);
			}
			for (const queryKey of ctx.queryKeys) {
				return queryClient.setQueriesData<
					InfiniteData<GetMessageDiscussionsResponse>
				>(
					{
						queryKey: [QUERY_KEYS.GET_MESSAGES, queryKey],
					},
					(oldData) => ({
						...oldData,
						pages:
							oldData?.pages.map((page) => ({
								...page,
								messages:
									page?.messages?.map((msg) => {
										if (msg.id_db === newMessage.id) {
											return {
												...msg,
												...("delivered" in newMessage
													? { delivered: !newMessage.delivered }
													: {}),
												...("strike" in newMessage
													? { strike: !newMessage.strike }
													: {}),
												...("sent" in newMessage
													? { sent: !newMessage.sent }
													: {}),
											};
										}
										return msg;
									}) ?? [],
							})) ?? [],
						pageParams: oldData?.pageParams ?? [1],
					}),
				);
			}
		},
	});
};

const getDeleteMessage = (queryClient: QueryClient) => {
	queryClient.setMutationDefaults([QUERY_KEYS.MUTATE_DELETE_MESSAGE], {
		mutationFn: (payload: GetDeleteMessageBodySchema) =>
			messageService.delete(payload),
		retry: 2,
		onMutate: async (newMessage) => {
			const channel_name = newMessage.channelName;
			if (!channel_name) return;
			await queryClient.cancelQueries({
				queryKey: [QUERY_KEYS.GET_MESSAGES, { channel_name }],
			});
			const infiniteMessages = queryClient.getQueryData<
				InfiniteData<GetMessageDiscussionsResponse>
			>([QUERY_KEYS.GET_MESSAGES, { channel_name }]);
			if (!infiniteMessages?.pages?.length) return;
			const messages = infiniteMessages.pages.flatMap((p) => p.messages);
			const message = messages.find((m) => m.id_db === newMessage.id);
			const queryKeys: Record<"channel_name" | "tags", string | undefined>[] = [
				{
					channel_name,
					tags: undefined,
				},
			];
			if (messages.length) {
				const tags = collectMessagesTags(messages);
				if (tags.length) {
					for (const msgTag of tags) {
						queryKeys.push({
							channel_name,
							tags: msgTag,
						});
					}
				}
			}

			if (!message) return;
			const optimisticMessage = {
				...message,
				is_deleted: true,
			};
			for (const queryKey of queryKeys) {
				queryClient.setQueryData<InfiniteData<GetMessageDiscussionsResponse>>(
					[QUERY_KEYS.GET_MESSAGES, queryKey],
					(oldData) =>
						infiniteDataReplace(
							oldData,
							"messages",
							"id_db",
							optimisticMessage.id_db,
							optimisticMessage,
						),
				);
			}
			return { queryKeys, optimisticMessage };
		},
		onError: (_error, newMessage, ctx) => {
			const channel_name = newMessage.channelName;
			if (!channel_name || !ctx) return;
			for (const queryKey of ctx.queryKeys) {
				queryClient.setQueryData<InfiniteData<GetMessageDiscussionsResponse>>(
					[QUERY_KEYS.GET_MESSAGES, queryKey],
					(oldData) =>
						infiniteDataReplace(
							oldData,
							"messages",
							"id_db",
							ctx.optimisticMessage.id_db,
							{
								...ctx.optimisticMessage,
								is_deleted: false,
							},
						),
				);
			}
		},
	});
};

const postReadMessage = (queryClient: QueryClient) => {
	queryClient.setMutationDefaults([QUERY_KEYS.MUTATE_READ_MESSAGE], {
		retry: 2,
		mutationFn: (payload: PostReadMessagePayloadSchema) =>
			messageService.read(payload),
		onMutate: async (payload) => {
			await queryClient.cancelQueries({
				queryKey: [
					QUERY_KEYS.GET_MESSAGES,
					{ channel_name: payload.channel_name },
				],
			});

			const userProfile = queryClient.getQueryData<MemberProfileResponse>([
				QUERY_KEYS.GET_MEMBER_PROFILE,
			]);
			const userId = userProfile?.user?.id;
			if (!userId || !payload.message_ids?.length) return;

			const queryKeys: Record<"channel_name" | "tags", string | undefined>[] = [
				{
					channel_name: payload.channel_name,
					tags: undefined,
				},
			];

			const resMessages = queryClient.getQueryData<
				InfiniteData<GetMessageDiscussionsResponse>
			>([QUERY_KEYS.GET_MESSAGES, queryKeys[0]]);

			if (resMessages?.pages?.length) {
				const messages = resMessages.pages.flatMap((p) => p.messages);
				if (messages.length) {
					const tags = collectMessagesTags(messages);
					if (tags.length) {
						for (const msgTag of tags) {
							queryKeys.push({
								channel_name: payload.channel_name,
								tags: msgTag,
							});
						}
					}
				}
			}

			if (!payload.message_id) {
				for (const queryKey of queryKeys) {
					queryClient.setQueryData<InfiniteData<GetMessageDiscussionsResponse>>(
						[QUERY_KEYS.GET_MESSAGES, queryKey],
						(oldData) => {
							if (!oldData) return oldData;
							return {
								...oldData,
								pages: oldData.pages.map((page) => ({
									...page,
									messages: page.messages.map((msg) =>
										payload.message_ids?.includes(msg.id)
											? {
													...msg,
													reads: Array.from(new Set([...msg.reads, userId])),
												}
											: {
													...msg,
													reads: Array.from(new Set([...msg.reads, userId])),
												},
									),
								})),
							};
						},
					);
				}
				return { queryKeys };
			}
			const prevMessage = queryClient.getQueryData<GetMessageResponse>([
				QUERY_KEYS.GET_MESSAGE,
				payload.message_id,
			]);
			queryClient.setQueryData<GetMessageResponse>(
				[QUERY_KEYS.GET_MESSAGE, payload.message_id],
				(oldData) => {
					if (!oldData) return oldData;
					return {
						...oldData,
						message: {
							...oldData.message,
							unread_thread_messages: 0,
							messages: oldData.message.messages.map((msg) =>
								payload.message_ids?.includes(msg.id)
									? {
											...msg,
											reads: Array.from(new Set([...msg.reads, userId])),
										}
									: {
											...msg,
											reads: Array.from(new Set([...msg.reads, userId])),
										},
							),
						},
					};
				},
			);
			return { queryKeys, prevMessage };
		},
		onError: (_error, payload, ctx) => {
			const userProfile = queryClient.getQueryData<MemberProfileResponse>([
				QUERY_KEYS.GET_MEMBER_PROFILE,
			]);
			const userId = userProfile?.user?.id;
			if (!userId) return;

			if (!payload.message_id && ctx?.queryKeys) {
				for (const queryKey of ctx.queryKeys) {
					queryClient.setQueryData<InfiniteData<GetMessageDiscussionsResponse>>(
						[QUERY_KEYS.GET_MESSAGES, queryKey],
						(oldData) => {
							if (!oldData) return oldData;
							return {
								...oldData,
								pages: oldData.pages.map((page) => ({
									...page,
									messages: page.messages.map((msg) =>
										payload.message_ids?.includes(msg.id)
											? {
													...msg,
													reads: msg.reads.filter(
														(readId) => readId !== userId,
													),
												}
											: msg,
									),
								})),
							};
						},
					);
				}
			}
			if (ctx?.prevMessage) {
				queryClient.setQueryData<GetMessageResponse>(
					[QUERY_KEYS.GET_MESSAGE, payload.message_id],
					ctx.prevMessage,
				);
			}
		},
	});
};

const getSyncMessage = (queryClient: QueryClient) => {
	queryClient.setMutationDefaults([QUERY_KEYS.MUTATE_SYNC_INBOX_MESSAGES], {
		retry: 2,
		mutationFn: (params: SyncMessageParamsSchema) =>
			messageService.sync(params),
		onSuccess: (res) => {
			const currentUser = queryClient.getQueryData<MemberProfileResponse>([
				QUERY_KEYS.GET_MEMBER_PROFILE,
			]);
			const myOutstandings = {
				...res,
				updated: res.updated.filter((u) => u.user.id === currentUser?.user?.id),
				deleted: res.deleted.filter((d) => d.user.id === currentUser?.user?.id),
			};
			const awaitingResponses = {
				...res,
				updated: res.updated.filter(
					(u) => currentUser?.user && u.user.id !== currentUser.user.id,
				),
				deleted: res.deleted.filter(
					(d) => currentUser?.user && d.user.id !== currentUser.user.id,
				),
			};
			queryClient.setQueryData<InfiniteData<GetMemberAlertsResponse>>(
				[QUERY_KEYS.GET_MY_ALERTS, { scope: "my_outstanding" }],
				(oldData) => ({
					...oldData,
					pages:
						oldData?.pages?.map((p) => ({
							...p,
							messages: applyDataChanges(
								p.messages || [],
								myOutstandings.updated,
								myOutstandings.deleted,
							),
						})) || [],
					pageParams: oldData?.pageParams || [1],
				}),
			);
			queryClient.setQueryData<InfiniteData<GetMemberAlertsResponse>>(
				[QUERY_KEYS.GET_MY_ALERTS, { scope: "awaiting_response" }],
				(oldData) => ({
					...oldData,
					pages:
						oldData?.pages?.map((p) => ({
							...p,
							messages: applyDataChanges(
								p.messages || [],
								awaitingResponses.updated,
								awaitingResponses.deleted,
							),
						})) || [],
					pageParams: oldData?.pageParams || [1],
				}),
			);
			useSyncStore.setState({
				inboxSyncLastUnix: unix(),
			});
		},
	});
};

const postAcceptMessage = (queryClient: QueryClient) => {
	queryClient.setMutationDefaults([QUERY_KEYS.MUTATE_MESSAGE_ACCEPT], {
		retry: 2,
		mutationFn: (params: MessageDiscussion) =>
			messageService.accept(params.id_db),
		onMutate: async (params) => {
			const channels = params.channel_name.split("-");
			const channelId = channels[1];
			const caseChannel = `case.${channelId}`;
			await queryClient.cancelQueries({
				queryKey: [QUERY_KEYS.GET_MESSAGES, { channel_name: caseChannel }],
			});
			const currentMessages = queryClient.getQueryData<
				InfiniteData<GetMessageDiscussionsResponse>
			>([QUERY_KEYS.GET_MESSAGES, { channel_name: caseChannel }]);

			const messages = currentMessages?.pages?.flatMap((p) => p.messages) ?? [];
			const message = messages.find((m) => m.id === params.id);

			if (message) {
				queryClient.setQueryData<InfiniteData<GetMessageDiscussionsResponse>>(
					[QUERY_KEYS.GET_MESSAGES, { channel_name: caseChannel }],
					(oldData) =>
						!oldData
							? oldData
							: infiniteDataRemove(oldData, "messages", "id", message.id),
				);
			}

			const currentAlerts = queryClient.getQueryData<
				InfiniteData<GetMemberAlertsResponse>
			>([
				QUERY_KEYS.GET_MY_ALERTS,
				{ scope: MEMBER_ALERT_SCOPE.MY_OUTSTANDING },
			]);

			const alerts = currentAlerts?.pages?.flatMap((p) => p.messages) ?? [];
			const alert = alerts.find((m) => m.id === params.id);

			if (alert) {
				queryClient.setQueryData<InfiniteData<GetMemberAlertsResponse>>(
					[
						QUERY_KEYS.GET_MY_ALERTS,
						{ scope: MEMBER_ALERT_SCOPE.MY_OUTSTANDING },
					],
					(oldData) =>
						!oldData
							? oldData
							: infiniteDataRemove(oldData, "messages", "id", alert.id),
				);
			}

			return { caseChannel, currentMessages, currentAlerts };
		},
		onError: (_, _params, ctx) => {
			if (!ctx) return;
			if (ctx.currentMessages?.pages?.length)
				queryClient.setQueryData<InfiniteData<GetMessageDiscussionsResponse>>(
					[QUERY_KEYS.GET_MESSAGES, { channel_name: ctx.caseChannel }],
					ctx.currentMessages,
				);
			if (ctx.currentAlerts?.pages?.length)
				queryClient.setQueryData<InfiniteData<GetMemberAlertsResponse>>(
					[
						QUERY_KEYS.GET_MY_ALERTS,
						{ scope: MEMBER_ALERT_SCOPE.MY_OUTSTANDING },
					],
					ctx.currentAlerts,
				);
		},
	});
};

const postDeclineMessage = (queryClient: QueryClient) => {
	queryClient.setMutationDefaults([QUERY_KEYS.MUTATE_MESSAGE_DECLINE], {
		retry: 2,
		mutationFn: (params: MessageDiscussion) =>
			messageService.decline(params.id_db),
		onMutate: async (params) => {
			const channels = params.channel_name.split("-");
			const channelId = channels[1];
			const caseChannel = `case.${channelId}`;
			await queryClient.cancelQueries({
				queryKey: [QUERY_KEYS.GET_MESSAGES, { channel_name: caseChannel }],
			});
			const currentMessages = queryClient.getQueryData<
				InfiniteData<GetMessageDiscussionsResponse>
			>([QUERY_KEYS.GET_MESSAGES, { channel_name: caseChannel }]);

			const messages = currentMessages?.pages?.flatMap((p) => p.messages) ?? [];
			const message = messages.find((m) => m.id === params.id);

			if (!message) return;

			queryClient.setQueryData<InfiniteData<GetMessageDiscussionsResponse>>(
				[QUERY_KEYS.GET_MESSAGES, { channel_name: caseChannel }],
				(oldData) =>
					!oldData
						? oldData
						: infiniteDataRemove(oldData, "messages", "id", message.id),
			);

			return { caseChannel, currentMessages };
		},
		onError: (_, _params, ctx) => {
			if (!ctx) return;
			queryClient.setQueryData<InfiniteData<GetMessageDiscussionsResponse>>(
				[QUERY_KEYS.GET_MESSAGES, { channel_name: ctx.caseChannel }],
				ctx.currentMessages,
			);
		},
	});
};

export const messageMutations = (queryClient: QueryClient) => {
	postCreateMessage(queryClient);
	patchUpdateMessage(queryClient);
	getDeleteMessage(queryClient);
	postReadMessage(queryClient);
	getSyncMessage(queryClient);
	postAcceptMessage(queryClient);
	postDeclineMessage(queryClient);
};
